import { Chip, Collapse, CollapsibleIconButton, styled, Tooltip, Typography } from '@chronosphereio/chrono-ui';
import { truncateText } from '@chronosphereio/core';
import { useMemo, useState } from 'react';
import { TooltipStaticContentWrapper } from '../../PanelTooltip';

export interface CommonLabelsSectionProps {
  commonLabels?: Record<string, string>;
  defaultExpanded?: boolean;
}

const MAX_LABEL_VALUE_LENGTH = 50;

export function CommonLabelsSection({ commonLabels, defaultExpanded = false }: CommonLabelsSectionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const chips = useMemo(() => {
    if (!commonLabels || Object.keys(commonLabels).length === 0) {
      return null;
    }

    return Object.entries(commonLabels).map(([label, value]) => {
      const truncatedValue = truncateText(value, MAX_LABEL_VALUE_LENGTH);
      const chipLabel = `${label}="${truncatedValue}"`;
      const fullLabel = `${label}="${value}"`;
      const isTruncated = value.length > MAX_LABEL_VALUE_LENGTH;
      const chip = <Chip key={label} label={chipLabel} />;
      return isTruncated ? (
        <Tooltip key={label} Content={fullLabel}>
          {chip}
        </Tooltip>
      ) : (
        chip
      );
    });
  }, [commonLabels]);

  if (!commonLabels || Object.keys(commonLabels).length === 0) {
    return null;
  }

  return (
    <TooltipStaticContentWrapper data-testid="common-labels-section">
      <CommonLabelsContainer>
        <CommonLabelsHeader onClick={() => setIsExpanded(!isExpanded)}>
          <CollapsibleIconButton collapsed={!isExpanded} ariaLabelSubject="common labels" size="small" noPadding />
          <Typography variant="caption" color="text.legacySecondary">
            Common labels ({Object.keys(commonLabels).length})
          </Typography>
        </CommonLabelsHeader>
        <Collapse in={isExpanded}>
          <CommonLabelsChipsContainer>{chips}</CommonLabelsChipsContainer>
        </Collapse>
      </CommonLabelsContainer>
    </TooltipStaticContentWrapper>
  );
}

const CommonLabelsContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

const CommonLabelsHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  cursor: 'pointer',
  paddingTop: '4px',
  paddingBottom: '4px',
});

const CommonLabelsChipsContainer = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '4px',
  paddingBottom: '4px',
});
